// ============================================================================
// game.js — GameManager: the meta-game state machine that sits above the battle.
// States: menu → briefing → battle → results (+ pause). Owns level progression,
// save/load (localStorage), options, and starting/stopping the sim. main.js only
// asks it whether to step the sim and to check the outcome each frame.
// ============================================================================

import { CAMPAIGN, rankFor } from './campaign.js';
import { loadLevel } from './levelController.js';
import { EV } from '../core/events.js';
import { Conquest, FACTIONS } from './conquest.js';
import { applyBiome } from '../render/biomes.js';

const SAVE_KEY = 'titanlord.save.v1';
const DEFAULT_OPTIONS = { volume: 0.5, sens: 0.0042, invertY: true };
const rankVal = (r) => ({ S: 4, A: 3, B: 2, C: 1 })[r] || 0;

// App Games ME rank SDK — resolves only when hosted on the portal (appgames.me
// serves /agme-rank.js); the catch keeps standalone runs fully working with
// every rank call a silent no-op.
let AGME = null;
const AGME_SDK_URL = '/agme-rank.js'; // variable specifier — keeps Rollup from resolving it at build time
import(/* @vite-ignore */ AGME_SDK_URL)
  .then((m) => {
    // Validate the module shape — a bad/mangled build must leave AGME null
    // (every call is optional-chained) rather than half-wired. A throw here
    // once froze checkOutcome's rAF loop on victory.
    if (typeof m?.initRank !== 'function' || typeof m?.reportMatch !== 'function') return;
    AGME = m;
    m.initRank('titanlord');
  })
  .catch(() => {});

export class GameManager {
  constructor(deps) {
    // deps: world, sim, screens, battlefield, followCam, audio, input,
    //       renderer, fx, crowd, worldmap (the last four drive biome theming + Conquest)
    Object.assign(this, deps);
    this.state = 'menu';
    this.levelIndex = 0;
    this.save = this._loadSave();
    this.options = { ...DEFAULT_OPTIONS, ...(this.save.options || {}) };
    this.applyOptions();
    this.screens.setController(this);
    if (this.worldmap) this.worldmap.setController(this);
    // ---- conquest layer ----
    this.conquest = null;       // built lazily from the save on first entry
    this._cq = null;            // active conquest battle context { regionId, mode }
    this._cqDef = null;         // its level def (for retry)
    this._freeRoam = false;     // "walk your lands" ride (no objectives; Esc returns to map)
    window.addEventListener('keydown', (e) => { if (e.code === 'Escape') this._onEsc(); });
  }

  // ---- persistence ----
  _loadSave() {
    try { return JSON.parse(localStorage.getItem(SAVE_KEY)) || {}; } catch { return {}; }
  }
  _defaults(s) { return { unlocked: s.unlocked || 0, ranks: s.ranks || {}, options: s.options || {}, conquest: s.conquest || null }; }
  _persist() { try { this.save.options = this.options; localStorage.setItem(SAVE_KEY, JSON.stringify(this.save)); } catch { /* ignore */ } }

  _playing(v) { document.body.classList.toggle('playing', v); }

  applyOptions() {
    this.followCam.invertY = this.options.invertY;
    this.followCam.sens = this.options.sens;
    if (this.audio) this.audio.setVolume(this.options.volume);
  }
  setOption(k, v) { this.options[k] = v; this.applyOptions(); this._persist(); }

  // ---- flow ----
  gotoMenu() {
    this.state = 'menu';
    this._playing(false);
    if (this.worldmap) this.worldmap.hide();
    this._cq = null; this._freeRoam = false;
    this.screens.setObjHudVisible(false);
    this.input.enabled = false;
    if (document.exitPointerLock) document.exitPointerLock();
    this.screens.showMenu(this.save = this._defaults(this.save));
  }
  startCampaign() { this.levelIndex = 0; this._enterBriefing(); }
  continueCampaign() { this.levelIndex = Math.min(this.save.unlocked || 0, CAMPAIGN.length - 1); this._enterBriefing(); }
  openLevels() { this.state = 'levels'; this.screens.showLevels(CAMPAIGN, this._defaults(this.save)); }
  selectLevel(i) { this.levelIndex = i; this._enterBriefing(); }
  openOptions() { this.screens.showOptions(this.options); }
  showPause() { this.screens.showPause(); }

  _enterBriefing() {
    this.state = 'briefing';
    this._playing(false);
    this._cq = null; this._freeRoam = false;
    if (this.worldmap) this.worldmap.hide();
    this.audio && this.audio.start();               // first user gesture chain
    const lv = CAMPAIGN[this.levelIndex];
    loadLevel(this.world, this.battlefield, lv);
    this._applyBiome(lv.biome, 'magmar');            // campaign is the ashen war vs Magmar
    this.screens.setObjHudVisible(false);
    this.input.enabled = false;
    this.screens.showBriefing(lv, this.levelIndex);
  }

  // ---- biome theming --------------------------------------------------------
  _theme() { return { renderer: this.renderer, fx: this.fx, crowd: this.crowd, battlefield: this.battlefield }; }
  _applyBiome(biome, enemyFactionKey) {
    if (!this.renderer) return;
    const fac = enemyFactionKey ? FACTIONS[enemyFactionKey] : null;
    applyBiome(biome || 'ashlands', this._theme(), fac ? { color: fac.crowdTint, ember: fac.crowdEmber } : null);
  }

  // ---- conquest layer -------------------------------------------------------
  openConquest() {
    if (!this.conquest) this.conquest = new Conquest(this.save.conquest);
    else if (this.conquest.over) this.conquest = new Conquest(null);   // war ended → start a fresh one
    this.cqPersist();
    this.state = 'worldmap';
    this._playing(false);
    this._cq = null; this._freeRoam = false;
    this.input.enabled = false;
    if (document.exitPointerLock) document.exitPointerLock();
    this.screens.hideAll();
    this.screens.setObjHudVisible(false);
    this.audio && this.audio.start();
    this.worldmap.show(this.conquest);
  }
  cqPersist() {
    if (!this.conquest) return;
    this.save = this._defaults(this.save);
    this.save.conquest = this.conquest.serialize();
    this._persist();
  }
  // player chose to fight a threat / assault a region himself
  cqMarch(regionId, mode) {
    const def = this.conquest.battleDef(regionId, mode);
    this._cq = { regionId, mode }; this._cqDef = def; this._freeRoam = false;
    this.worldmap.hide();
    this._enterConquestBattle(def);
  }
  cqWalk(regionId) {
    const def = this.conquest.walkDef(regionId);
    this._cq = { regionId, mode: 'walk' }; this._cqDef = def; this._freeRoam = true;
    this.worldmap.hide();
    this._enterConquestBattle(def);
  }
  _enterConquestBattle(def) {
    this.audio && this.audio.start();
    loadLevel(this.world, this.battlefield, def);
    this._applyBiome(def.biome, def.enemyFaction);
    this.state = 'battle';
    this._playing(true);
    this.screens.hideAll();
    this.screens.setObjHudVisible(!this._freeRoam);
    this.input.enabled = true;
    if (this.input.requestLock) this.input.requestLock();
    this.world.events.emit(EV.ANNOUNCE, { text: def.name.toUpperCase(), tone: this._freeRoam ? 'good' : 'bad' });
  }
  _endConquestBattle(win) {
    const ctx = this._cq; this._cq = null; this._freeRoam = false;
    if (ctx && ctx.mode !== 'walk') this.conquest.applyResult(ctx.regionId, ctx.mode, win);
    this.cqPersist();
    this.state = 'cqresult';
    this._playing(false);
    this.input.enabled = false;
    if (document.exitPointerLock) document.exitPointerLock();
    this.screens.setObjHudVisible(false);
    // let the VICTORY/DEFEAT banner flash, then drop back to the realm map
    setTimeout(() => { if (this.state === 'cqresult') this.openConquest(); }, 1600);
  }

  beginBattle() {
    this.state = 'battle';
    this._playing(true);
    this.screens.hideAll();
    this.screens.setObjHudVisible(true);
    this.input.enabled = true;
    if (this.input.requestLock) this.input.requestLock();
    const lv = CAMPAIGN[this.levelIndex];
    this.world.events.emit(EV.ANNOUNCE, { text: lv.name.toUpperCase(), tone: 'bad' });
  }

  retryLevel() { if (this._cq && this._cqDef) { this._enterConquestBattle(this._cqDef); return; } this._enterBriefing(); }
  nextLevel() { if (this.levelIndex < CAMPAIGN.length - 1) { this.levelIndex++; this._enterBriefing(); } else this.gotoMenu(); }

  pause() {
    if (this.state !== 'battle') return;
    this.state = 'paused';
    this._playing(false);
    this.input.enabled = false;
    if (document.exitPointerLock) document.exitPointerLock();
    this.screens.setObjHudVisible(false);
    this.screens.showPause();
  }
  resume() {
    if (this.state !== 'paused') return;
    this.state = 'battle';
    this._playing(true);
    this.screens.hideAll();
    this.screens.setObjHudVisible(true);
    this.input.enabled = true;
    if (this.input.requestLock) this.input.requestLock();
  }
  _onEsc() {
    // a free-roam ride has no fail state — Esc just returns to the realm map
    if (this.state === 'battle' && this._freeRoam) { this._cq = null; this._freeRoam = false; this.openConquest(); return; }
    if (this.state === 'battle') this.pause();
    else if (this.state === 'paused') this.resume();
    else if (this.state === 'worldmap') { this.worldmap.hide(); this.gotoMenu(); }
  }

  isBattle() { return this.state === 'battle'; }

  // called by main each frame after any sim steps
  checkOutcome() {
    if (this.state !== 'battle' || !this.world.outcome) return;
    const win = this.world.outcome === 'win';
    // Rank reporting — single-fire per battle (the state leaves 'battle' below
    // either way). Free-roam rides have no outcome stakes and never report.
    if (!this._freeRoam) {
      AGME?.reportMatch({ won: win });
      if (this.world.killCount >= 300) AGME?.unlock('three-hundred-slain');
    }
    if (this._cq) { this._endConquestBattle(win); return; }   // conquest battle → back to the realm map
    const lv = CAMPAIGN[this.levelIndex];
    const rank = win ? rankFor(lv, this.world.killCount, this.world.levelTime) : '-';
    const stats = {
      kills: this.world.killCount, time: this.world.levelTime,
      combo: this.world.maxCombo, reason: this.world.failReason, rank,
    };
    if (win) {
      this.save = this._defaults(this.save);
      this.save.unlocked = Math.max(this.save.unlocked, Math.min(this.levelIndex + 1, CAMPAIGN.length - 1));
      const prev = this.save.ranks[lv.id];
      if (!prev || rankVal(rank) > rankVal(prev)) this.save.ranks[lv.id] = rank;
      this._persist();
      if (lv.id === 'l14') AGME?.unlock('dynast-slayer');
    }
    this.state = 'results';
    this._playing(false);
    this.input.enabled = false;
    if (document.exitPointerLock) document.exitPointerLock();
    this.screens.setObjHudVisible(false);
    const hasNext = this.levelIndex < CAMPAIGN.length - 1;
    // let the VICTORY/DEFEAT banner flash first
    setTimeout(() => { if (this.state === 'results') this.screens.showResults(win, lv, this.levelIndex, stats, hasNext); }, 1500);
  }
}
